"use client"

import {
  CenterAligned,
  classNames,
  FlexColumn,
  ImageStack,
  Spinner,
  TextBody,
  TextHeading,
  useIsLessThanLg,
} from "@opensea/ui-kit"
import { useWindowSize } from "@opensea/ui-kit/hooks"
import Confetti from "react-confetti"
import { createPortal } from "react-dom"

export type TransactionFullScreenProps = {
  confetti?: boolean
  title: string
  body?: string
  images?: (string | null | undefined)[]
  bottom?: React.ReactNode
  loading?: boolean
  monochrome?: boolean
}

export function TransactionFullScreen({
  confetti,
  title,
  body,
  images,
  loading,
  bottom,
  monochrome,
}: TransactionFullScreenProps) {
  const isMobile = useIsLessThanLg()
  const { width } = useWindowSize()

  return (
    <>
      {confetti
        ? createPortal(
            <div
              className="fixed top-0 left-0 z-modal h-screen w-screen"
              style={{ pointerEvents: "none" }}
            >
              <Confetti
                gravity={0.2}
                key={width}
                numberOfPieces={100}
                recycle
                wind={0}
              />
            </div>,
            document.body,
          )
        : null}

      <CenterAligned
        className={classNames(
          "relative z-50 py-10",
          monochrome ? "saturate-0" : "",
        )}
      >
        <FlexColumn className="items-center gap-4 text-center">
          {(images?.length || 0) > 0 && (
            <ImageStack
              animate={monochrome ? undefined : "fade-in"}
              overrides={{
                animation: {
                  position: {
                    left: {
                      className: "delay-400",
                    },
                    right: {
                      className: "delay-200",
                    },
                  },
                },
              }}
              show={monochrome ?? true}
              size={isMobile ? 180 : 360}
              srcs={images?.map(image => image ?? undefined)}
            />
          )}
          <FlexColumn className="gap-6">
            {title ? (
              <TextHeading size="lg">
                <h2>{title}</h2>
              </TextHeading>
            ) : null}
            {body ? <TextBody color="text-secondary">{body}</TextBody> : null}
          </FlexColumn>

          {loading ? (
            <FlexColumn>
              <Spinner size="md" />
            </FlexColumn>
          ) : null}
          {bottom}
        </FlexColumn>
      </CenterAligned>
    </>
  )
}
